/*
 * Helper methods for rendering React components inside an AngularJS page.
 */

angular.module('licensure.helpers')
    .factory('ngReactBridge', ['$window', '$state',
        /**
        * @param {Window=} $window
        * @param {any=} $state
        */
        function ($window, $state) {
            if (!$window) {
                $window = window;
            }

            /**
             * Returns true if the css file is already loaded.
             *
             * @param {string} url
            */
            var isCssLoaded = function (url) {
                if (!url || url.indexOf("data:") === 0) {
                    return false;
                }

                var existingLinkElements = $window.document.getElementsByTagName('link');
                for (var i = 0; i < existingLinkElements.length; i++) {
                    if (existingLinkElements[i].href === url) {
                        return true;
                    }
                }

                var existingStyleElements = $window.document.getElementsByTagName('style');
                for (var i = 0; i < existingStyleElements.length; i++) {
                    if (existingStyleElements[i].getAttribute("data-href") === url) {
                        return true;
                    }
                }

                return false;
            }

            /**
             * Loads a css file into the current HTML document.
             * 
             * @param {string} url
             * @return {Promise<HTMLLinkElement | undefined>}
             */
            var loadCssAsync = function (url) {
                if (isCssLoaded(url)) {
                    return Promise.resolve();
                }

                var linkElement = $window.document.createElement('link');
                linkElement.rel = 'stylesheet';
                linkElement.href = url;
                $window.document.head.appendChild(linkElement);
                return Promise.resolve(linkElement);
            };

            /**
             * Returns true if the css file is already loaded.
             *
             * @param {string} url
            */
            var isScriptLoaded = function (url) {
                if (!url || url.indexOf("data:") === 0) {
                    return false;
                }

                var existingScriptElements = $window.document.getElementsByTagName('script');
                for (var i = 0; i < existingScriptElements.length; i++) {
                    if (existingScriptElements[i].src === url || existingScriptElements[i].getAttribute("data-src") === url) {
                        return true;
                    }
                }
                return false;
            }

            /**
             * Loads a javascript file into the current HTML document and returns a promise that resolves when it is loaded.
             * 
             * @param {string} url
             * @return {Promise<HTMLScriptElement | undefined>}
             */
            var loadScriptAsync = function (url) {
                if (isScriptLoaded(url)) {
                    return Promise.resolve();
                }

                var scriptElement = $window.document.createElement('script');
                var loadScriptPromise = new Promise(function (resolve, reject) {
                    scriptElement.async = true;
                    scriptElement.onload = resolve;
                    scriptElement.onerror = reject;
                    scriptElement.src = url;
                    $window.document.body.appendChild(scriptElement);
                });
                var timeoutPromise = new Promise(function (resolve) {
                    setTimeout(resolve, 10000);
                });
                return Promise.race([loadScriptPromise, timeoutPromise]).then(function () { return scriptElement });
            };

            // Offline Cache support for loading scripts/css.
            // We want this stuff to be available offline, but the main application uses cache manifest.
            // Instead of trying to fit our dynamic scripts into that, we'll do our own thing using the newer Cache API.
            // If the main application switches from cache manifest to service workers, then we can probably do all of the cache work there and get rid of all of this garbage.
            var openCacheAsync = function () {
                var cacheNameBase = "lms-licensure-cache-26.04.0.0";

                return $window.caches.open(cacheNameBase);
            }

            /**
            * @param {Cache} cache
            * @param {string} url
            */
            var cacheGetOrAddAsync = function (cache, url) {
                // Try the cache first, fall back to network
                return cache.match(url)
                    .then(function (response) {
                        if (response) {
                            return response.text();
                        }
                        return cache.add(url)
                            .then(function () {
                                return cache.match(url)
                            })
                            .then(function (response) {
                                return response.text();
                            });
                    });

                // Try the network first, fall back to cache.
                return cache.add(url)
                    .catch(function () {
                        // It's fine if the fetch for adding to the cache failed, that's when we fall back to just reading from the cache.
                        return undefined;
                    })
                    .then(function () {
                        return cache.match(url)
                    })
                    .then(function (cacheResponse) {
                        if (!cacheResponse) {
                            return undefined;
                        }
                        return cacheResponse.text();
                    });
            };

            var originalLoadScriptAsync = loadScriptAsync;

            /**
            * @param {string} script
            * @param {string} fromSrc
            */
            var loadScriptTextAsync = function (script, fromUrl) {
                if (fromUrl && isScriptLoaded(fromUrl)) {
                    return Promise.resolve();
                }

                var scriptElement = $window.document.createElement('script');
                if (fromUrl) {
                    scriptElement.setAttribute("data-src", fromUrl);
                }
                scriptElement.text = script;
                $window.document.body.appendChild(scriptElement);
                return Promise.resolve(scriptElement);
            };

            loadScriptAsync = function (url) {
                return openCacheAsync()
                    .then(function (cache) {
                        return cacheGetOrAddAsync(cache, url);
                    })
                    .then(function (scriptText) {
                        if (!scriptText) {
                            throw new Error("Failed to cache load script from: " + url);
                        }
                        return loadScriptTextAsync(scriptText, url);
                    })
                    .catch(function (err) {
                        console.error("Fallback because of script cache load error", err);
                        return originalLoadScriptAsync(url);
                    });
            };

            var originalLoadCssAsync = loadCssAsync;

            /**
            * @param {string} css
            * @param {string} fromHref
            */
            var loadCssText = function (css, fromUrl) {
                if (fromUrl && isCssLoaded(fromUrl)) {
                    return;
                }

                var styleElement = $window.document.createElement('style');
                styleElement.type = "text/css";
                styleElement.textContent = css;
                if (fromUrl) {
                    styleElement.setAttribute("data-href", fromUrl);
                }
                $window.document.head.appendChild(styleElement);
                return styleElement;
            };

            loadCssAsync = function (url) {
                return openCacheAsync()
                    .then(function (cache) {
                        return cacheGetOrAddAsync(cache, url);
                    })
                    .then(function (cssText) {
                        if (!cssText) {
                            throw new Error("Failed to cache load css from: " + url);
                        }
                        return loadCssText(cssText, url);
                    })
                    .catch(function (err) {
                        console.error("Fallback because of css cache load error", err);
                        return originalLoadCssAsync(url);
                    });
            };

            /**
             * Initializes the current HTML page in preparation for rendering a react component,
             * and returns a promise that resolves when it is ready.
             * 
             * @param {string} resource
             * @param {string} basename
             * @returns {Promise<void>}
             */
            var initializeAsync = function (resource, basename) {
                // Ensure we have a hash in our url (or things get weird with react router)
                var href = $window.location.href;
                var hashIndex = href.indexOf("#");
                if (hashIndex === -1) {
                    window.location.replace(href + "#/" + basename);
                }

                /** @type {string} */
                var apiLocation = $window.apiLocation || "";
                // update this version on release
                var version = '26.04.0.0';
                var cssUrl = apiLocation + '/api/ClientAppBundles/css/' + resource + '?v=' + version;
                var loadCssPromise = loadCssAsync(cssUrl, $window);

                var scriptUrl = apiLocation + '/api/ClientAppBundles/js/' + resource + '?v=' + version;
                var loadScriptPromise = loadScriptAsync(scriptUrl, $window);

                return Promise.all([loadScriptPromise, loadCssPromise]);
            };

            /**
             * Creates and returns render options for an LMS react bundle.
             * 
             * @param {string} basename
             * @returns options for rendering a page (as defined in the React app)
             */
            var createRenderOptions = function (basename, portal) {
                /** @type {string} */
                var apiLocation = $window.apiLocation || "";

                var pathBase = $window.location.pathname;
                if (typeof pathBase !== "string") {
                    /** @type { { app?: string } } */
                    var paths = $window.paths || {};
                    var appPath = paths.app || "";
                    var pathBase = appPath.replace(/\/app(\/)?$/i, '$1');
                    if (!pathBase || pathBase.length === 0) {
                        pathBase = appPath;
                    }
                }

                var loginUrl = $state && portal
                    ? pathBase + $state.href('portal.menus.login', {}, { absolute: false })
                    : pathBase + "#/login";
                var homeUrl = pathBase + "#/";

                var renderPageOptions = {
                    rootElementId: "react-root",
                    pathBase: pathBase,
                    apiUrl: apiLocation + '/',
                    loginUrl: loginUrl,
                    homeUrl: homeUrl,
                    routerBasename: basename,
                    angularJsCompatibilityMode: true
                };
                return renderPageOptions;
            };

            /* eslint-disable valid-jsdoc */
            /**
             * Creates and returns an AngularJS directive for rendering a react element.
             * 
             * @param {string} basename
             * @param {function(ReturnType<typeof createRenderOptions>): void} renderFunc
             * @param {function(ReturnType<typeof createRenderOptions>): void} cleanupFunc
             * @returns an AngularJS directive for rendering a React element
             */
            /* eslint-enable valid-jsdoc */
            var createNgReactDirective = function (basename, portal, renderFunc, cleanupFunc) {
                var renderOptions = createRenderOptions(basename, portal);
                return {
                    restrict: 'E',
                    replace: true,

                    /**
                     * @param {any} scope
                     * @param {Element[]=} elem
                     * @param {any=} attrs
                     */
                    link: function (scope, elem, attrs) {
                        try {
                            if (elem && elem.length > 0) {
                                renderOptions.rootElement = elem[0];
                            }
                            renderFunc(renderOptions);
                        } catch (err) {
                            console.error("React render failed", err);
                            throw err;
                        }

                        if (cleanupFunc) {
                            // Based on the cleanup in the directive from https://github.com/ngReact/ngReact
                            scope.$on('$destroy', function () {
                                try {
                                    if (!attrs.onScopeDestroy) {
                                        cleanupFunc(renderOptions);
                                    } else {
                                        scope.$eval(attrs.onScopeDestroy, {
                                            unmountComponent: cleaner.bind(this, renderOptions)
                                        });
                                    }
                                } catch (err) {
                                    console.error("React cleanup error", err);
                                }
                            });
                        }
                    }
                };
            };

            return {
                initializeAsync: initializeAsync,
                createNgReactDirective: createNgReactDirective
            };
        }]);
